import { useState, useEffect } from 'react';
import Questions from '../components/Questions';
import { convertCmToFeetInches, convertHeightToCm } from '../utils/utils';
import { AnimatePresence, motion } from "framer-motion";

const Height = ({
  subGroup,
  validationErrors,
  setValidationErrors,
  inputRefs,
  handleInputBlur,
  setQuestions,
  firstLevelArea,
  secondLevelArea,
  subArea,
  inputBlurTriggered,
  savingQuestionId,
  fetchAreaNames,
  client,
  product,
  language,
  setIsDataSaved,
}) => {




  const [heightValues, setHeightValues] = useState({
    cm: '',
    feet: '',
    inches: '',
  });

  const [isCmSelected, setIsCmSelected] = useState(true); // To manage toggle state

  const cmQuestion = subGroup.subAreaCompositionDtoList.find(q => q.code === 'Q_HEIGHT');
  const feetQuestion = subGroup.subAreaCompositionDtoList.find(q => q.code === 'Q_HEIGHT_FEET');
  const inchesQuestion = subGroup.subAreaCompositionDtoList.find(q => q.code === 'Q_HEIGHT_INCHES');
  const unitToggleQuestion = subGroup.subAreaCompositionDtoList.find(q => q.uiRenderType === 'HEIGHT_UNIT');

  useEffect(() => {
    if (cmQuestion && feetQuestion && inchesQuestion && unitToggleQuestion) {
      setIsCmSelected(unitToggleQuestion.value === unitToggleQuestion.possibleValues[0].value);

      setHeightValues({
        cm: cmQuestion.value || '',
        feet: feetQuestion.value || '',
        inches: inchesQuestion.value || '',
      });
    }
  }, [cmQuestion, feetQuestion, inchesQuestion, unitToggleQuestion]);




  const handleHeightChange = async (updatedField, value) => {
    let updatedValues = { ...heightValues };

    // Ensure value is properly converted to a number or empty string
    const numericValue = value === '' || value === null ? '' : parseInt(value, 10);

    if (updatedField === 'cm') {
      if (numericValue === '' || numericValue === null) {
        updatedValues = { cm: '', feet: '', inches: '' }; // Reset all values
      } else {
        updatedValues.cm = numericValue;
        const { feet, inches } = convertCmToFeetInches(numericValue);
        updatedValues.feet = feet;
        updatedValues.inches = inches;
      }
    } else if (updatedField === 'feet' || updatedField === 'inches') {
      updatedValues[updatedField] = numericValue;

      // Default inches and feet to 0 if empty or null, but only for the calculation
      const feetValue = updatedValues.feet === '' || updatedValues.feet === null ? 0 : updatedValues.feet;
      const inchesValue = updatedValues.inches === '' || updatedValues.inches === null ? 0 : updatedValues.inches;

      // If both feet and inches are empty or null, set cm to null
      if (feetValue === 0 && inchesValue === 0) {
        updatedValues = { cm: '', feet: '', inches: '' }; // Reset all values
      } else {
        const cm = convertHeightToCm(feetValue, inchesValue);
        updatedValues.cm = cm;
      }
    }

    setHeightValues(updatedValues);

    const updatedQuestions = [
      { ...cmQuestion, value: updatedValues.cm === '' ? null : updatedValues.cm },
      { ...feetQuestion, value: updatedValues.feet === '' ? null : updatedValues.feet },
      { ...inchesQuestion, value: updatedValues.inches === '' ? null : updatedValues.inches },
    ];

    await handleInputBlur(updatedQuestions);
  };


  const handleToggleChange = async (updatedQuestion) => {
    const newValue = updatedQuestion.value;
    // Small delay to allow exit transition
    setTimeout(() => {
      setIsCmSelected(newValue === unitToggleQuestion.possibleValues[0].value);
    }, 100);
    await handleInputBlur(updatedQuestion);
  };



  // if language Francais put pi, po instead of ft and in
  useEffect(() => {
    const style = document.createElement('style');

    const styles = `
      .code_Q_HEIGHT_FEET .form-group:before {
        content: 'pi';
      }  
      .code_Q_HEIGHT_INCHES .form-group:before {
        content: 'po';
      }
    `;

    if (style.styleSheet) {
      style.styleSheet.cssText = styles;
    } else {
      style.appendChild(document.createTextNode(styles));
    }


    if (language === 'Français') {
      document.head.appendChild(style);
    }

    return () => {
      if (language === 'Français') {
        document.head.removeChild(style);
      }
    };
  }, [language]);

  return (
    <div className={subGroup.uiRenderType}>

<div className="questions">
      <AnimatePresence mode="popLayout" initial={false}>
        {subGroup.subAreaCompositionDtoList.map((question) => {
          const isVisible =
            (isCmSelected && question.code === 'Q_HEIGHT') ||
            (!isCmSelected &&
              (question.code === 'Q_HEIGHT_FEET' || question.code === 'Q_HEIGHT_INCHES')) ||
            ['HEIGHT_UNIT'].includes(question.uiRenderType);

          return (
            <motion.div
              key={`question-${question.uniqueId}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className={isVisible ? question.code : 'd-none'}
              style={{ display: 'block' }} // forces layout consistency like CSSTransition did
            >
              {/* CM version */}
              {isCmSelected && question.code === 'Q_HEIGHT' && (
                <Questions
                  key={question.uniqueId}
                  question={question}
                  validationErrors={validationErrors}
                  setValidationErrors={setValidationErrors}
                  inputRefs={inputRefs}
                  setQuestions={setQuestions}
                  handleInputBlur={async (updatedQuestion) => {
                    await handleHeightChange('cm', updatedQuestion.value);
                  }}
                  firstLevelArea={firstLevelArea}
                  subArea={subArea}
                  secondLevelArea={secondLevelArea}
                  inputBlurTriggered={inputBlurTriggered}
                  savingQuestionId={savingQuestionId}
                  fetchAreaNames={fetchAreaNames}
                  client={client}
                  product={product}
                  language={language}
                  setIsDataSaved={setIsDataSaved}
                />
              )}

              {/* Imperial version */}
              {!isCmSelected &&
                (question.code === 'Q_HEIGHT_FEET' || question.code === 'Q_HEIGHT_INCHES') && (
                  <Questions
                    key={question.uniqueId}
                    question={question}
                    validationErrors={validationErrors}
                    setValidationErrors={setValidationErrors}
                    inputRefs={inputRefs}
                    setQuestions={setQuestions}
                    handleInputBlur={async (updatedQuestion) => {
                      const value = updatedQuestion.value;
                      if (question.code === 'Q_HEIGHT_FEET') {
                        await handleHeightChange('feet', value);
                      } else {
                        await handleHeightChange('inches', value);
                      }
                    }}
                    firstLevelArea={firstLevelArea}
                    subArea={subArea}
                    secondLevelArea={secondLevelArea}
                    inputBlurTriggered={inputBlurTriggered}
                    savingQuestionId={savingQuestionId}
                    fetchAreaNames={fetchAreaNames}
                    client={client}
                    product={product}
                    language={language}
                    setIsDataSaved={setIsDataSaved}
                  />
                )}

              {/* HEIGHT_UNIT Toggle */}
              {['HEIGHT_UNIT'].includes(question.uiRenderType) && (
                <Questions
                  key={question.uniqueId}
                  question={question}
                  validationErrors={validationErrors}
                  setValidationErrors={setValidationErrors}
                  inputRefs={inputRefs}
                  setQuestions={setQuestions}
                  handleInputBlur={async (updatedQuestion) => {
                    await handleToggleChange(updatedQuestion);
                  }}
                  firstLevelArea={firstLevelArea}
                  subArea={subArea}
                  secondLevelArea={secondLevelArea}
                  inputBlurTriggered={inputBlurTriggered}
                  savingQuestionId={savingQuestionId}
                  fetchAreaNames={fetchAreaNames}
                  client={client}
                  product={product}
                  language={language}
                  setIsDataSaved={setIsDataSaved}
                />
              )}
            </motion.div>
          );
        })}
      </AnimatePresence>


    </div>
    </div>
  );
};

export default Height;


